import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Modal } from '@mui/material';
import { getFolderTree } from '@/api/document.api';
import { Button } from '@/components/UI/Button';
import { FormSelect } from '@/components/UI/Select';
import { InputWrapper } from '@/components/Container/AuthContainer/styles';
import { ModalActions, ModalForm, ModalTitle } from '../style';
import { moveDocuments, MoveDocument, MoveResult } from './batchMove';

type Props = {
  documents: MoveDocument[];
  currentFolderId: number | null;
  onClose: () => void;
  onMoved: (ids: string[]) => Promise<void>;
};

const BatchMoveModal = ({ documents, currentFolderId, onClose, onMoved }: Props) => {
  // @ts-ignore
  const { t } = useTranslation();
  const [options, setOptions] = useState<{ value: string; text: string }[]>([]);
  const [pending, setPending] = useState(documents);
  const [results, setResults] = useState<MoveResult[]>([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const inFlight = useRef(false);
  const { control, handleSubmit } = useForm({ defaultValues: { targetFolderId: '' } });

  useEffect(() => {
    let active = true;
    getFolderTree().then(({ data }) => {
      if (!active) return;
      if (!data.success) throw new Error();
      const flatten = (folders: FolderTreeNode[], prefix = ''): { value: string; text: string }[] =>
        folders.flatMap(folder => {
          const path = `${prefix}${folder.name}`;
          return [
            ...(folder.id !== currentFolderId ? [{ value: String(folder.id), text: path }] : []),
            ...flatten(folder.children || [], `${path} / `),
          ];
        });
      setOptions([
        ...(currentFolderId !== null ? [{ value: 'root', text: t('pages.docLibrary.batchMove.root') }] : []),
        ...flatten(data.data),
      ]);
    }).catch(() => { if (active) setError(t('pages.docLibrary.batchMove.loadFailed')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [currentFolderId, t]);

  const submit = handleSubmit(async ({ targetFolderId }) => {
    if (inFlight.current || !pending.length) return;
    if (!options.some(option => option.value === targetFolderId)) {
      setError(t('pages.docLibrary.batchMove.chooseFolder'));
      return;
    }
    inFlight.current = true;
    setBusy(true);
    setError('');
    try {
      const next = await moveDocuments(pending, targetFolderId === 'root' ? null : Number(targetFolderId));
      setResults(previous => [...previous.filter(result => result.success), ...next]);
      setPending(next.filter(result => !result.success));
      const moved = next.filter(result => result.success).map(result => result.id);
      if (moved.length) {
        try { await onMoved(moved); }
        catch { setError(t('pages.docLibrary.batchMove.refreshFailed')); }
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  });

  return (
    <Modal open onClose={() => { if (!inFlight.current) onClose(); }} aria-labelledby="batch-move-title">
      <ModalForm onSubmit={submit}>
        <ModalTitle id="batch-move-title">{t('pages.docLibrary.batchMove.title', { count: documents.length })}</ModalTitle>
        <fieldset disabled={busy || loading || !pending.length} style={{ border: 0, padding: 0, margin: 0 }}>
          <InputWrapper style={{ height: 'auto' }}>
            <FormSelect control={control} name="targetFolderId" data={options}
              inputLabel={t('pages.docLibrary.batchMove.destination')}
              selectLabel={t('pages.docLibrary.batchMove.destination')} />
          </InputWrapper>
        </fieldset>
        {error && <p role="alert">{error}</p>}
        {busy && <p role="status">{t('pages.docLibrary.batchMove.moving')}</p>}
        {results.length > 0 && <ul aria-live="polite" style={{ maxHeight: '240px', overflowY: 'auto' }}>
          {results.map(result => <li key={result.id}>{result.name}: {t(result.success
            ? 'pages.docLibrary.batchMove.success' : 'pages.docLibrary.batchMove.failed')}</li>)}
        </ul>}
        <ModalActions>
          {pending.length > 0 && <Button type="submit" variant="primary" disabled={busy || loading || !options.length}>
            {t(results.length ? 'pages.docLibrary.batchMove.retryFailed' : 'pages.docLibrary.fileMenu.moveTo')}
          </Button>}
          <Button type="button" variant="primary-invert" disabled={busy} onClick={onClose}>{t('buttons.close')}</Button>
        </ModalActions>
      </ModalForm>
    </Modal>
  );
};

export default BatchMoveModal;
